import React, { useState, useRef, useEffect, FunctionComponent } from "react";
import { v4 as uuid } from "uuid";

import Header from "./components/Header";
import NavBar from "./components/NavBar";
import {
  getArea,
  getTimeStamp,
  currentDateTime,
  isPast
} from "./components/functions";
import Joyride, { CallBackProps, STATUS } from "react-joyride";
import { joyrideStyles, joyrideSteps } from "./components/helpers-joyride";
import "./App.scss";

import {
  fetchStoreData,
  uploadProducts,
  fetchBakeData,
  fetchUserInfo,
  fetchProductionsData
} from "./backendServices";

import { useSnackbar } from "notistack";
import Spinner from "./components/Spinner";

import { BakeryItem, ProductionsData } from "./types";
import Settings from "./Settings";
import Feedback from "./Feedback";
import ForecastEdit from "./ForecastEdit";
import WavePlanner from "./WavePlanner";
import HighRiskRotation from "./HighRiskRotation";

const defaultSubmitButtonText = "Submit";
const failureMessage =
  "Failed uploading to database. This is likely due to Wi-Fi. Please try again. If the problem persists, please contact IT Support.";

interface MainProps {}

const Main: FunctionComponent<MainProps> = () => {
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null);
  const [bakeTableState, setBakeTableState] = useState<any>([]);
  const [showSettings, setShowSettings] = useState(false);
  const [showWaves, setShowWaves] = useState(false);
  const [showHighRiskRotation, setShowHighRiskRotation] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showForecastEdit, setShowForecastEdit] = useState(false);
  const isAllUnitsZero =
    Array.isArray(bakeTableState) &&
    bakeTableState.every((item: BakeryItem) => item.product_units === 0);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [storeName, setStoreName] = useState("");
  const [storeId, setStoreId] = useState("");
  const [storeTrial, setStoreTrial] = useState(false);
  const [tempCheckTrial, setTempCheckTrial] = useState(false);
  const [productName, setProductName] = useState<string | null>(null);
  const [productSKU, setProductSKU] = useState<string | null>(null);
  const [productTemp, setProductTemp] = useState<string | null>(null);
  const [plannerHour, setPlannerHour] = useState<string | null>(null);
  const [quantity, setQuantity] = useState<number>(0);
  const [made, setMade] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isProcessingError, setIsProcessingError] = useState(false);
  const [submitButtonText, setSubmitButtonText] = useState(
    defaultSubmitButtonText
  );
  const [userInfo, setUserInfo] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [clearTempChecks, setClearTempChecks] = useState<(number | null)[]>([]);

  const getCookie = (cookieName: string) => {
    return document.cookie
      .split("; ")
      .find((row) => row.startsWith(cookieName + "="))
      ?.split("=")[1];
  };

  const checkAndSetCookie = (cookieName: string) => {
    if (!getCookie(cookieName)) {
      const date = new Date();
      date.setTime(date.getTime() + 31556952000);
      const expires = "expires=" + date.toUTCString();
      document.cookie = cookieName + "=cookie set; " + expires + ";path=/";
      return true;
    } else {
      return false;
    }
  };

  const joyrideCookieCheck = () => {
    return checkAndSetCookie("flythrough-min-waves");
  };

  const [joyrideRun, setJoyrideRun] = useState<boolean>(false);

  const queryParameters = new URLSearchParams(window.location.search);
  const [isFOH] = useState(queryParameters.get("foh") ? true : false);

  const showSettingsHandler = () => {
    setShowWaves(false);
    setShowHighRiskRotation(false);
    setShowSettings(true);
    setShowFeedback(false);
    setShowForecastEdit(false);
    window.dataLayer.push({
      event: "Navigation",
      value: "Settings",
      bakery: storeName,
      bakery_area: getArea(isFOH),
      time: getTimeStamp()
    });
  };

  const showFeedbackHandler = (
    productName: string,
    PLU: string,
    plannerHour: string,
    quantity: number,
    made: number
  ) => {
    setShowFeedback(true);
    setProductName(productName);
    setProductSKU(PLU);
    setPlannerHour(plannerHour);
    setQuantity(quantity);
    setMade(made);
  };

  const showForecastEditHandler = (
    productName: string,
    PLU: string,
    plannerHour: string,
    quantity: number,
    made: number
  ) => {
    setShowForecastEdit(true);
    setProductName(productName);
    setProductSKU(PLU);
    setPlannerHour(plannerHour);
    setQuantity(quantity);
    setMade(made);
  };

  const clearFeedbackHandler = () => {
    setProductName(null);
    setProductSKU(null);
    setPlannerHour(null);
    setQuantity(0);
    setMade(null);
  };

  const hideFeedbackHandler = () => {
    setShowFeedback(false);
    setShowForecastEdit(false);
  };

  const showWavesHandler = () => {
    setShowWaves(true);
    setShowHighRiskRotation(false);
    setShowSettings(false);
    setShowFeedback(false);
    setShowForecastEdit(false);
    setProductName(null);
    setProductSKU(null);
    setProductTemp(null);
    setPlannerHour(null);
    setQuantity(0);
    setMade(null);
    window.dataLayer.push({
      event: "Navigation",
      value: "Wave planner",
      bakery: storeName,
      bakery_area: getArea(isFOH),
      time: getTimeStamp()
    });
  };

  const showHighRiskRotationHandler = () => {
    setShowWaves(false);
    setShowHighRiskRotation(true);
    setShowSettings(false);
    setShowFeedback(false);
    setShowForecastEdit(false);
    setProductName(null);
    setProductSKU(null);
    setProductTemp(null);
    setPlannerHour(null);
    setQuantity(0);
    setMade(null);
    window.dataLayer.push({
      event: "Navigation",
      value: "High risk rotation",
      bakery: storeName,
      bakery_area: getArea(isFOH),
      time: getTimeStamp()
    });
  };

  const showHomeHandler = () => {
    setShowWaves(false);
    setShowHighRiskRotation(false);
    setShowSettings(false);
    setShowFeedback(false);
    setShowForecastEdit(false);
    setProductName(null);
    setProductSKU(null);
    setProductTemp(null);
    setPlannerHour(null);
    window.dataLayer.push({
      event: "Navigation",
      value: "Home",
      bakery: storeName,
      bakery_area: getArea(isFOH),
      time: getTimeStamp()
    });
  };

  const getTotalQty = () => {
    let totalQty = 0;
    bakeTableState.forEach((item: { product_units: number }) => {
      totalQty += item.product_units;
    });
    return totalQty.toString().padStart(3, "0");
  };

  const refreshWavePlanner = () => {
    setRefreshKey((prev) => prev + 1);
  };

  useEffect(() => {
  const goTo = localStorage.getItem("afterReload");
  if (goTo === "highRisk") {
    showHighRiskRotationHandler();
    localStorage.removeItem("afterReload");
  }
}, []);

  useEffect(() => {
    setTimeout(() => {
      setJoyrideRun(joyrideCookieCheck());
    }, 2000);

    const refreshAtMidnight = (store: string) => {
      const now = new Date();
      const tonight = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        23,
        0,
        0
      );
      const msTillMidnight = tonight.getTime() - now.getTime();
      if (msTillMidnight > 0) {
        setTimeout(() => {
          const closedTabsMessage =
            "Refresh in progress—loading the latest live version.";
          enqueueSnackbar(closedTabsMessage, {
            variant: "error",
            transitionDuration: { enter: 0, exit: 0 },
            action: (key) => (
              <button onClick={() => closeSnackbar(key)}>Dismiss</button>
            ),
            style: {
              top: "50vh",
              transform: "translateY(-50%)"
            },
            persist: true,
            anchorOrigin: { horizontal: "center", vertical: "top" },
            onClose: (event, reason, key) => {
              window.location.reload();
            }
          });
          window.dataLayer.push({
            event: "Close tabs warning",
            bakery: store,
            bakery_area: getArea(isFOH),
            time: getTimeStamp()
          });
        }, msTillMidnight);
      }
    };
    

    fetchUserInfo()
      .then((userInfo) => {
        setUserInfo(userInfo);
        return Promise.all([fetchBakeData(), fetchStoreData()]);
      })
      .then(([bakeData, storeData]) => {
        setBakeTableState(bakeData);
        setStoreName(storeData["store_name"]);
        setStoreId(storeData["store_id"]);
        setStoreTrial(storeData["wave_planner_trial"]);
        setTempCheckTrial(storeData["temp_check_trial"]);
        refreshAtMidnight(storeData["store_name"]);
        setLoading(false);
        triggerRefresh();
        console.log(
          'storeData["time_difference"]: ',
          storeData["time_difference"]
        );
        (window as any).timeOffset = storeData.time_difference;
        storeData["time_difference"] > 10000 &&
          window.dataLayer.push({
            event: "Time difference",
            bakery: storeName,
            bakery_area: getArea(isFOH),
            time: getTimeStamp(),
            value: `Time difference: ${storeData["time_difference"]}`
          });
      })
      .catch((error) => {
        console.error("bakeData, storeData error: ", error);
        setLoading(false);
        setLoadingError(true);
      });
  }, []);

  const resetBakeTable = () => {
    const resetBakeryItems: BakeryItem[] = bakeTableState.map(
      (item: BakeryItem) => ({ ...item, product_units: 0 })
    );
    console.log("resetBakeTable() setBakeTableState...");
    setBakeTableState(resetBakeryItems);
    window.dataLayer.push({
      event: "Reset bakery table",
      bakery: storeName,
      bakery_area: getArea(isFOH),
      value: "0",
      total_qty: getTotalQty(),
      time: getTimeStamp()
    });
  };

  const onSubmit = () => {
    setIsProcessing(true);
    handleUploadProducts();
    window.dataLayer.push({
      event: "Send to till",
      bakery: storeName,
      bakery_area: getArea(isFOH),
      value: "Sending started",
      time: getTimeStamp(),
      total_qty: getTotalQty()
    });
  };

  const refreshData = async () => {
    try {
      const bakeData = await fetchBakeData();
      console.log("refreshData() setBakeTableState...");
      setBakeTableState(bakeData);
    } catch (error) {
      console.error(error);
    }
  };

  let failureAttempts = 0;

  const handleSuccess = (uploadBakeTableState: BakeryItem[]) => {
    setClearTempChecks(
      uploadBakeTableState
        .map((item: BakeryItem) => {
          return item.product_units > 0 && item.temp_check ? item.PLU : null;
        })
        .filter((PLU) => PLU !== null)
    );
    setSubmitButtonText("Items sent");
    failureAttempts = 0;
    setTimeout(() => {
      setSubmitButtonText(defaultSubmitButtonText);
    }, 5000);
    enqueueSnackbar("Please take finished products to display", {
      variant: "success",
      autoHideDuration: 5000,
      transitionDuration: { enter: 0, exit: 0 },
      style: {
        top: "50vh",
        transform: "translateY(-50%)"
      },
      anchorOrigin: { horizontal: "center", vertical: "top" }
    });
    triggerRefresh();
    window.dataLayer.push({
      event: "Send to till",
      bakery: storeName,
      bakery_area: getArea(isFOH),
      value: "Success",
      time: getTimeStamp(),
      total_qty: getTotalQty()
    });
  };

  const handleFailure = (retryMessage: string) => {
    setIsProcessing(false);
    setSubmitButtonText(retryMessage);
    if (failureAttempts < 10) {
      setIsProcessingError(true);
      setTimeout(() => {
        setIsProcessing(true);
        setIsProcessingError(false);
        handleUploadProducts();
      }, 5000);
      failureAttempts++;
      window.dataLayer.push({
        event: "Send to till",
        bakery: storeName,
        bakery_area: getArea(isFOH),
        value: `${retryMessage}: ${failureAttempts}`,
        time: getTimeStamp(),
        total_qty: getTotalQty()
      });
    } else {
      setIsProcessing(false);
      setSubmitButtonText("Try again");
      failureAttempts = 0;
      enqueueSnackbar(failureMessage, {
        variant: "error",
        transitionDuration: { enter: 0, exit: 0 },
        action: (key) => (
          <button onClick={() => closeSnackbar(key)}>Dismiss</button>
        ),
        style: {
          top: "50vh",
          transform: "translateY(-50%)"
        },
        persist: true,
        anchorOrigin: { horizontal: "center", vertical: "top" },
        onClose: (event, reason, key) => {
          setSubmitButtonText(defaultSubmitButtonText);
        }
      });
      window.dataLayer.push({
        event: "Send to till",
        bakery: storeName,
        bakery_area: getArea(isFOH),
        value: "Total Failure after 10 tries",
        time: getTimeStamp(),
        total_qty: getTotalQty()
      });
    }
  };

  const handleUploadProducts = async () => {
    if (!idempotencyKey) {
      setIdempotencyKey(uuid());
    }
    const key = idempotencyKey!;
    const uploadBakeTableState: BakeryItem[] = bakeTableState.map(
      (item: BakeryItem) => {
        const { batch_size, start_hour, end_hour, temp, ...rest } = item;
        return {
          ...rest,
          store_name: storeName,
          store_id: storeId,
          timestamp: currentDateTime(),
          already_made_waves: item.product_units
            ? [...item.already_made_waves, item.product_units]
            : item.already_made_waves,
          sent_date: item.product_units ? Date.now() : 0
        };
      }
    );

    try {
      const response = await uploadProducts(uploadBakeTableState, key);
      if (response.status === 409) {
        await refreshData();
        setIsProcessing(false);
        setIdempotencyKey(null);
        handleSuccess(uploadBakeTableState);
        return;
      }
      if (response.status === 200) {
        try {
          await refreshData();
          setIsProcessing(false);

          if (response.data.response === "ok") {
            setIdempotencyKey(null);
            handleSuccess(uploadBakeTableState);
          } else {
            handleFailure("Re-trying database");
          }
        } catch (error) {
          setIsProcessing(false);
          failureAttempts = 0;
          enqueueSnackbar(failureMessage, {
            variant: "error",
            autoHideDuration: 5000
          });
          console.error(error);
        }
      } else {
        setIsProcessing(false);
        enqueueSnackbar(failureMessage, {
          variant: "error",
          autoHideDuration: 5000
        });
      }
    } catch (error) {
      setTimeout(() => {
        handleFailure("No Wi-Fi, re-trying");
      }, 5000);
      console.error(error);
    }
  };

  const [productionsData, setProductionsData] = useState<ProductionsData[]>([]);
  const [rotationAlert, setRotationAlert] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const intervalRef = useRef<any>(null);

  const triggerRefresh = () => {
    //console.log("triggerRefresh() at: ", getTimeStamp());
    //console.log("refresh:", refresh);
    setRefresh((prev) => prev + 1);
  };

  const getEndTime = (timestamp: string, rotation: number | null) => {
    if (rotation !== null) {
      const date = new Date(timestamp);
      date.setMinutes(date.getMinutes() + rotation);
      const hours = date.getHours();
      const minutes = date.getMinutes();
      return `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}`;
    } else {
      return;
    }
  };

  useEffect(() => {
    if (tempCheckTrial && bakeTableState.length > 0) {
      fetchProductionsData()
        .then((productionsData) => {
          const modifiedProductionsData = productionsData.map((production) => {
            return {
              ...production,
              end_time: getEndTime(
                production.timestamp,
                bakeTableState.filter(
                  (item: BakeryItem) => item.PLU === production.PLU
                )[0]?.target_fresh ?? null
              ),
              target_fresh: bakeTableState.filter(
                (item: BakeryItem) => item.PLU === production.PLU
              )[0]?.target_fresh
            };
          });
          const sortedProductionsData = modifiedProductionsData.sort((a, b) => {
            if (
              a["high_risk_rotation_clearance"] &&
              b["high_risk_rotation_clearance"]
            ) {
              return ("" + a["end_time"]).localeCompare("" + b["end_time"]);
            } else if (!a["end_time"]) {
              return 1;
            } else if (!b["end_time"]) {
              return -1;
            } else if (a["high_risk_rotation_clearance"]) {
              return 1;
            } else if (b["high_risk_rotation_clearance"]) {
              return -1;
            } else {
              return ("" + a["end_time"]).localeCompare("" + b["end_time"]);
            }
          });
          setProductionsData(modifiedProductionsData);

          setRotationAlert(
            modifiedProductionsData.some(
              (production) =>
                !production.high_risk_rotation_clearance &&
                production.end_time &&
                isPast(production.end_time)
            )
          );

          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }

          intervalRef.current = setInterval(() => {
            triggerRefresh();
          }, 300000);

          return () => {
            console.log("Clearing previous interval...");
            clearInterval(intervalRef.current);
          };
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }, [refresh]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh"
        }}
      >
        <Spinner />
      </div>
    );
  } else if (loadingError) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh"
        }}
      >
        <div>
          <p>Looks like you're having trouble logging in, please ensure:</p>
          <ul>
            <li>You have accessed this link through BreadHeads</li>
            <li>You are connected your Bakery wifi</li>
            <li>Try refreshing the page a few times</li>
          </ul>
          <p>
            Contact your ops manager if you continue to experience this issue
          </p>
        </div>
      </div>
    );
  }

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, type } = data;
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

    window.dataLayer.push({
      event: "Joyride",
      value: status,
      step: data.index.toString(),
      type: type,
      bakery: storeName,
      bakery_area: getArea(isFOH),
      time: getTimeStamp()
    });

    if (finishedStatuses.includes(status)) {
      setJoyrideRun(false);
    }
  };

  
const backgroundFetchData = () => {
  localStorage.setItem("afterReload", "highRisk");
  window.location.reload();
};

  return (
    <div
      className={`App reporter ${isFOH ? "reporter--foh" : "reporter--boh"}`}
    >
      <Joyride
        steps={joyrideSteps}
        styles={joyrideStyles}
        run={joyrideRun}
        showProgress={false}
        continuous
        callback={handleJoyrideCallback}
        debug={false}
        disableScrolling
        showSkipButton
        hideCloseButton
        locale={{ skip: "Quit", last: "Finish" }}
      />
      {showSettings ? (
        <Settings userInfo={userInfo} handleShowHome={showHomeHandler} />
      ) : showWaves ? (
        <WavePlanner
          storeName={storeName}
          isFOH={isFOH}
          bakeTableState={bakeTableState}
          setBakeTableState={setBakeTableState}
          handleShowForcastEdit={showForecastEditHandler}
          refreshKey={refreshKey}
          handleShowHome={showHomeHandler}
        />
      ) : showHighRiskRotation ? (
        <HighRiskRotation
          storeName={storeName}
          isFOH={isFOH}
          isLoading={loading}
          handleShowHome={showHomeHandler}
          bakeTableState={bakeTableState}
          productionsData={productionsData}
          triggerRefresh={triggerRefresh}
          onExpireCountdown={backgroundFetchData}
        />
      ) : (
        <Header
          isProcessing={isProcessing}
          isProcessingError={isProcessingError}
          submitButtonText={submitButtonText}
          isAllUnitsZero={isAllUnitsZero}
          resetBakeTable={resetBakeTable}
          onSubmit={onSubmit}
          bakeTableState={bakeTableState}
          setBakeTableState={setBakeTableState}
          isFOH={isFOH}
          storeName={storeName}
          storeId={parseInt(storeId)}
          storeTrial={storeTrial}
          tempCheckTrial={tempCheckTrial}
          handleShowFeedback={showFeedbackHandler}
          clearTempChecks={clearTempChecks}
        />
      )}
      <NavBar
        storeName={storeName}
        handleShowSettings={showSettingsHandler}
        handleShowHome={showHomeHandler}
        handleShowWaves={showWavesHandler}
        handleShowHighRiskRotation={showHighRiskRotationHandler}
        isFOH={isFOH}
        setJoyrideRun={setJoyrideRun}
        tempCheckTrial={tempCheckTrial}
        rotationAlert={rotationAlert}
        disableHelp={
          showWaves ||
          showHighRiskRotation ||
          showSettings ||
          showFeedback ||
          showForecastEdit
        }
      />
      <Feedback
        storeName={storeName}
        isFOH={isFOH}
        productName={productName}
        productSKU={productSKU}
        plannerHour={plannerHour}
        quantity={quantity}
        made={made}
        handleClearFeedback={clearFeedbackHandler}
        handleHideFeedback={hideFeedbackHandler}
        showFeedback={showFeedback}
      />
      <ForecastEdit
        storeName={storeName}
        isFOH={isFOH}
        productName={productName}
        productSKU={productSKU ?? ""}
        plannerHour={plannerHour ?? ""}
        quantity={quantity}
        made={made}
        handleClearFeedback={clearFeedbackHandler}
        handleHideFeedback={hideFeedbackHandler}
        showForecastEdit={showForecastEdit}
        refreshWavePlanner={refreshWavePlanner}
      />
    </div>
  );
};

export default Main;
